import React, { memo, useCallback } from 'react';

import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { GripIcon } from '@sharedCR/coreDesignSystem/icons';
import { DeleteIcon, EditPenIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { formsTestIds } from '../../../../forms.testIds';

import {
  COLUMN_HEADER_VARIANT,
  DATA_ROW_VARIANT,
  DRAG_ICON_STYLE,
  DROPPABLE_ID,
  ICON_STYLE,
} from './SigningRulesTable.constants';

const SigningRulesTable = ({ rules = [], onAdd, onEdit, onDelete, onDragEnd }) => {
  const handleEdit = useCallback(
    index => () => {
      onEdit(index);
    },
    [onEdit],
  );

  const handleDelete = useCallback(
    index => () => {
      onDelete(index);
    },
    [onDelete],
  );

  return (
    <Box className="flex flex-col gap-8">
      {!!rules.length && (
        <Box className="flex flex-col gap-4">
          <Box className="flex items-center gap-8 px-8">
            <Box className="w-18 shrink-0" />
            <Typography className="w-24 shrink-0" variant={COLUMN_HEADER_VARIANT}>
              #
            </Typography>
            <Typography className="flex-1 min-w-0" variant={COLUMN_HEADER_VARIANT}>
              Expression
            </Typography>
            <Typography className="w-120 shrink-0" variant={COLUMN_HEADER_VARIANT}>
              Capacity
            </Typography>
            <Typography className="w-60 shrink-0" variant={COLUMN_HEADER_VARIANT}>
              E-Sign
            </Typography>
            <Box className="w-64 shrink-0" />
          </Box>

          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable direction="vertical" droppableId={DROPPABLE_ID}>
              {provided => (
                <Box ref={provided.innerRef} {...provided.droppableProps}>
                  {rules.map((rule, index) => (
                    <Draggable draggableId={rule.id} index={index} key={rule.id}>
                      {provided => (
                        <Box
                          className="flex items-center gap-8 px-8 py-4 rounded hover:bg-gray-50"
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          <Box
                            className="w-18 shrink-0 flex items-center"
                            {...provided.dragHandleProps}
                          >
                            <GripIcon style={DRAG_ICON_STYLE} />
                          </Box>
                          <Typography className="w-24 shrink-0" variant={DATA_ROW_VARIANT}>
                            {index + 1}
                          </Typography>
                          <Typography
                            className="flex-1 min-w-0 truncate"
                            title={rule.expression}
                            variant={DATA_ROW_VARIANT}
                          >
                            {rule.expression}
                          </Typography>
                          <Typography className="w-120 shrink-0" variant={DATA_ROW_VARIANT}>
                            {rule.capacity?.name}
                          </Typography>
                          <Typography className="w-60 shrink-0" variant={DATA_ROW_VARIANT}>
                            {rule.includeInESigning ? 'Yes' : 'No'}
                          </Typography>
                          <Box className="flex w-64 shrink-0">
                            <IconButton
                              data-testid={formsTestIds.getSigningRulesTableEditTestId(index)}
                              size="small"
                              onClick={handleEdit(index)}
                            >
                              <EditPenIcon style={ICON_STYLE} />
                            </IconButton>
                            <IconButton
                              data-testid={formsTestIds.getSigningRulesTableDeleteTestId(index)}
                              size="small"
                              onClick={handleDelete(index)}
                            >
                              <DeleteIcon style={ICON_STYLE} />
                            </IconButton>
                          </Box>
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        </Box>
      )}

      <Button
        data-testid={formsTestIds.SIGNING_RULES_TABLE_ADD_RULE_BUTTON}
        size="small"
        variant="outlined"
        onClick={onAdd}
      >
        + Add Rule
      </Button>
    </Box>
  );
};

SigningRulesTable.propTypes = {
  onAdd: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  rules: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      expression: PropTypes.string.isRequired,
      capacity: PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.string,
      }),
      includeInESigning: PropTypes.bool,
    }),
  ),
};

export default memo(SigningRulesTable);
